/**
 * RoadmapCommentThread — the discussion panel at the bottom of the roadmap
 * editor modal.
 *
 * Reads `roadmap/{id}/comments` directly from Firestore (realtime, oldest
 * first) and posts through the `addRoadmapComment` callable. An unsaved item
 * has no doc to hang comments off, so the caller renders the
 * "save to enable comments" placeholder instead of mounting this.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import {
  addRoadmapComment,
  subscribeToRoadmapComments,
} from '../../services/firebase';
import { formatAbsolute } from '../../utils/roadmapDates';
import type { RoadmapAuthor, RoadmapComment } from '../../types';

const AUTHOR_LABELS: Record<RoadmapAuthor, string> = {
  james: 'James',
  orchestrator: 'Orchestrator',
};

const AUTHOR_BADGE: Record<RoadmapAuthor, string> = {
  james: 'bg-brand-100 text-brand-700',
  orchestrator: 'bg-purple-100 text-purple-700',
};

interface RoadmapCommentThreadProps {
  roadmapId: string;
  /** Stamped on new comments. Admin panel always posts as James. */
  author?: RoadmapAuthor;
}

export function RoadmapCommentThread({
  roadmapId,
  author = 'james',
}: RoadmapCommentThreadProps) {
  const [comments, setComments] = useState<RoadmapComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!roadmapId) return;
    setLoading(true);
    setLoadError(false);
    const unsubscribe = subscribeToRoadmapComments(
      roadmapId,
      (rows) => {
        setComments(rows);
        setLoading(false);
      },
      () => {
        setLoadError(true);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [roadmapId]);

  const handlePost = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await addRoadmapComment(roadmapId, text, author);
      // onSnapshot delivers the new comment; just clear the box.
      setDraft('');
    } catch (err) {
      console.error('Failed to add roadmap comment:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to add comment',
      );
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="border-t border-gray-200 pt-4 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Comments</h3>
        {!loading && !loadError && (
          <span className="text-xs text-gray-500">{comments.length}</span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 italic py-3">
          Loading comments…
        </div>
      ) : loadError ? (
        <div className="text-sm text-red-600 py-3">
          Couldn’t load comments. Check your connection and reopen the item.
        </div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-3">
          No comments yet.
        </div>
      ) : (
        <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
          {comments.map((comment) => {
            const commentAuthor = comment.author ?? 'james';
            return (
              <li key={comment.id} className="text-sm">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      AUTHOR_BADGE[commentAuthor] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {AUTHOR_LABELS[commentAuthor] ?? commentAuthor}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatAbsolute(comment.createdAt)}
                  </span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap break-words">
                  {comment.text ?? ''}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Add a comment…"
          disabled={posting}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePost}
          disabled={!draft.trim() || posting}
        >
          {posting ? 'Posting…' : 'Add comment'}
        </Button>
      </div>
    </div>
  );
}
